// components/industry-templates/StartupEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Rocket, TrendingUp, Users, Lightbulb, Award } from 'lucide-react';

export const StartupEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Pitch Competition */}
      {industryData?.pitch_competition && (
        <Card className="border-0 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Rocket className="w-8 h-8" style={{ color: theme.primary }} />
              Startup Pitch Competition
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-6 bg-white/10 rounded-xl border-2" style={{ borderColor: theme.primary }}>
              <div className="text-center mb-6">
                <p className="text-5xl font-bold mb-2" style={{ color: theme.primary }}>
                  {industryData.pitch_competition.prize_pool}
                </p>
                <p className="text-white text-xl">Total Prize Pool</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold text-white">{industryData.pitch_competition.slots}</p>
                  <p className="text-sm text-slate-400">Pitch Slots</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold text-white">{industryData.pitch_competition.pitch_duration}</p>
                  <p className="text-sm text-slate-400">Per Pitch</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-3xl font-bold text-white">{industryData.pitch_competition.judges}</p>
                  <p className="text-sm text-slate-400">Judges</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Investor Network */}
      {industryData?.investors && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6" style={{ color: theme.primary }} />
              Meet Investors & VCs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.investors.map((investor: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/5 rounded-lg border border-white/10 hover:border-purple-500/50 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl" style={{ backgroundColor: theme.primary }}>
                      💼
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-white text-lg">{investor.name}</h4>
                      <p className="text-slate-400 mb-2">{investor.firm}</p>
                      <div className="flex gap-2 flex-wrap">
                        <Badge style={{ backgroundColor: theme.primary }}>
                          {investor.focus_area}
                        </Badge>
                        {investor.ticket_size && (
                          <Badge variant="outline" className="border-white/20 text-white">
                            Ticket: {investor.ticket_size}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workshops */}
      {industryData?.workshops && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lightbulb className="w-6 h-6" style={{ color: theme.primary }} />
              Startup Workshops
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.workshops.map((workshop: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-2">{workshop.title}</h4>
                  <p className="text-sm text-slate-400 mb-2">{workshop.speaker}</p>
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>{workshop.duration}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">{workshop.level}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mentorship */}
      {industryData?.mentorship_sessions && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(99, 102, 241, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              1-on-1 Mentorship
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white text-lg mb-3">Connect with experienced entrepreneurs and mentors</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>{industryData.mentorship_sessions.mentors}</p>
                  <p className="text-sm text-slate-400">Mentors</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>{industryData.mentorship_sessions.duration}</p>
                  <p className="text-sm text-slate-400">Session Time</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
